import { ChevronDown, CircleAlert } from 'lucide-react';
import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { cx } from '../cx.js';

export interface OpzioneSelect {
  value: string;
  label: string;
}

export interface NativeSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  options: OpzioneSelect[];
  hint?: string;
  error?: string;
  /** Prima voce vuota (es. "Tutti"). */
  placeholder?: string;
}

/** Menu a tendina nativo: funziona ovunque, anche da tastiera e su telefono. */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(function NativeSelect(
  { label, options, hint, error, placeholder, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const hintId = `${selectId}-aiuto`;
  const errorId = `${selectId}-errore`;
  return (
    <div className={cx('flex flex-col gap-2', className)}>
      <label htmlFor={selectId} className="t-label text-ink">
        {label}
      </label>
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={[error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined}
          className={cx(
            'w-full h-12 rounded-md bg-surface text-ink pl-4 pr-10 border appearance-none t-body',
            error ? 'border-error' : 'border-border-strong',
          )}
          {...rest}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown aria-hidden size={20} strokeWidth={1.75} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted" />
      </div>
      {error && (
        <p id={errorId} role="alert" className="t-body-small text-error flex items-start gap-1">
          <CircleAlert aria-hidden size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}
      {hint && (
        <p id={hintId} className="t-body-small text-ink-muted">
          {hint}
        </p>
      )}
    </div>
  );
});
